import React, { CSSProperties } from "react";

import { dec2hex, htmlEncode } from "../../../util";
import { TooltipData } from "../hooks/useTooltip";

type Props = {
  title?: string;
  tooltipData: TooltipData;
  width: number;
  closeTooltip: () => void;
  changeTooltipPage: (direction: "prev" | "next") => void;
};

const VisualizationTooltip = React.forwardRef<HTMLDivElement, Props>(
  (
    { title, tooltipData, closeTooltip, changeTooltipPage, width },
    tooltipRef
  ) => {
    const { names, instructions, pages, position } = tooltipData;

    if (!names) return null;

    const is64Bit = String(title).indexOf("PE32+ ") === 0;
    const { total: totalPages, currentPage: currentPageNum } = pages;
    const showControls = totalPages > 1;
    const { top: positionTop, left: positionLeft } = position;

    const controlBtnClasses = {
      prev: currentPageNum === 1 ? "disabled" : "",
      next: currentPageNum === totalPages ? "disabled" : ""
    };

    const tableOffset: CSSProperties = {
      transform: `translateX(-${(currentPageNum - 1) * 100}%)`
    };

    return (
      <div
        ref={tooltipRef}
        className="file-visualization__tooltip"
        style={{
          width,
          top: positionTop ?? undefined,
          left: positionLeft ?? undefined
        }}
      >
        <span
          className="file-visualization__tooltip-close-btn"
          onClick={closeTooltip}
        >
          <span className="icon-close"></span>
        </span>

        {showControls && (
          <div className="file-visualization__tooltip-controls">
            <span
              className={`file-visualization__tooltip-btn ${controlBtnClasses.prev}`}
              onClick={() => changeTooltipPage("prev")}
            >
              <span className="icon-prev"></span>
            </span>
            <div className="file-visualization__tooltip-slide-num">
              {currentPageNum} / {totalPages}
            </div>
            <span
              className={`file-visualization__tooltip-btn ${controlBtnClasses.next}`}
              onClick={() => changeTooltipPage("next")}
            >
              <span className="icon-next"></span>
            </span>
          </div>
        )}

        <div className="file-visualization__tables-wrap" style={tableOffset}>
          {names.map((nameItem, nameIndex) => {
            const instructionItemExists =
              instructions && instructions.length > nameIndex;

            return (
              <table key={nameIndex} className="file-visualization__table">
                <caption>{htmlEncode(nameItem.name)}</caption>
                <thead>
                  <tr>
                    <th className="p-address">address</th>
                    <th className="p-disassembly">disassembly</th>
                    <th className="p-comment">comment</th>
                  </tr>
                </thead>
                <tbody>
                  {instructions?.length && instructionItemExists ? (
                    instructions[nameIndex]?.map(
                      (instructionItem, instructionIndex) => {
                        const address = dec2hex(
                          parseInt(instructionItem.address, 10),
                          is64Bit
                        );
                        const { comment, disasm } = instructionItem;

                        return (
                          <tr key={instructionIndex}>
                            <td>0x{address}</td>
                            <td>{disasm}</td>
                            {comment ? (
                              <td
                                className="file-visualization__table-comment-cell"
                                title={htmlEncode(comment)}
                              >
                                <span>{htmlEncode(comment)}</span>
                              </td>
                            ) : (
                              <td>&nbsp;</td>
                            )}
                          </tr>
                        );
                      }
                    )
                  ) : (
                    <tr>
                      <td style={{ width: "200px" }}>No Data Available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            );
          })}
        </div>
      </div>
    );
  }
);

export default VisualizationTooltip;
